// src/routes/admin/FormulasPage.tsx
import AdminLayout from "../../../components/AdminHeader";
import { useUsers, UserRow } from "../../../hooks/useUsers";
import { useMemo, useState } from "react";
import { api } from "../../../api/client";
import "./index.css";
import AdminParfumesTable from "../../../components/AdminFormulaTable";
import { User } from "../../../api/types";

type Parfume = {
  id: string;
  name: string;
  img?: string;
  description?: string;
  question?: string;
  formula?: any;
  createdAt: number;
};

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    draft: "Старт",
    formula_production: "Формулы собираются",
    in_client: "У клиента",
    approved: "Одобрено клиентом",
    in_production: "Производится",
    in_pack: "Упаковка",
    ready_to_ship: "Готово к отправке",
    shipped: "Отправлено",
    delivered: "Доставлено",
  };
  return (
    <span className={`badge badge--${status}`}>{map[status] || status}</span>
  );
}

function NextActionLabel(status: string) {
  if (status === "approved") return "Начать производство";
  if (status === "in_production") return "Отправить на упаковку";
}

export default function ProductionPage() {
  const statuses = useMemo(() => ["approved", "in_production"], []);
  const { users, loading, err, setUsers } = useUsers(statuses);

  const [confirmUser, setConfirmUser] = useState<UserRow | null>(null);

  // раскрытые строки: userId -> {parfumes, loading, error, open}
  const [openRows, setOpenRows] = useState<
    Record<
      string,
      {
        open: boolean;
        parfumes: Parfume[];
        loading: boolean;
        error: string;
      }
    >
  >({});

  async function toggleOpen(u: UserRow) {
    setOpenRows((prev) => {
      const row = prev[u.id] || {
        open: false,
        parfumes: [],
        loading: false,
        error: "",
      };
      const next = { ...prev, [u.id]: { ...row, open: !row.open } };
      return next;
    });

    // при первом открытии — фетчим парфюмы
    const row = openRows[u.id];
    const needLoad = !row || (row && row.parfumes.length === 0);
    const nowOpen = !row?.open;
    if (nowOpen && needLoad) {
      setOpenRows((prev) => ({
        ...prev,
        [u.id]: { open: true, parfumes: [], loading: true, error: "" },
      }));
      try {
        const data = await api<{
          ok: true;
          parfumes: Parfume[];
          nextCursor?: string;
        }>(`/admin/user/${u.id}/parfumes?limit=20`, { method: "GET" });
        setOpenRows((prev) => ({
          ...prev,
          [u.id]: {
            open: true,
            parfumes: data.parfumes,
            loading: false,
            error: "",
          },
        }));
      } catch (e: any) {
        setOpenRows((prev) => ({
          ...prev,
          [u.id]: {
            open: true,
            parfumes: [],
            loading: false,
            error: e.message || String(e),
          },
        }));
      }
    }
  }

  async function saveParfume(
    userId: string,
    p: Parfume,
    patch: Partial<Parfume>
  ) {
    const body: any = {};
    if (typeof patch.name !== "undefined") body.name = patch.name;
    if (typeof patch.description !== "undefined")
      body.description = patch.description;
    if (typeof patch.question !== "undefined") body.question = patch.question;
    if (typeof patch.img !== "undefined") body.img = patch.img;
    if (typeof patch.formula !== "undefined") body.formula = patch.formula;

    // оптимистично в таблице
    setOpenRows((prev) => {
      const row = prev[userId];
      if (!row) return prev;
      const updated = row.parfumes.map((x) =>
        x.id === p.id ? { ...x, ...patch } : x
      );
      return { ...prev, [userId]: { ...row, parfumes: updated } };
    });

    try {
      await api(`/admin/user/${userId}/parfume/${p.id}`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
    } catch (e: any) {
      // в реале стоит перезагрузить строку, но тут просто сообщим
      alert(e.message || "Не удалось сохранить");
    }
  }

  async function advance(u: UserRow) {
    const next = u.status === "approved" ? "in_production" : "in_pack";
    try {
      const us = await api<{ ok: true; user: User }>("/admin/me", {
                    method: "GET",
                  });
      const adminId = us.user.id
      await api("/admin/set-status", {
        method: "POST",
        body: JSON.stringify({ userId: u.id, status: next, adminId }),
      });
      setUsers((prev) =>
        prev.map((x) => (x.id === u.id ? { ...x, status: next } : x))
      );
    } catch (e) {
      alert("Не удалось обновить статус");
    } finally {
      setConfirmUser(null);
    }
  }

  return (
    <AdminLayout>
      <div className="adminHead">
        <h1>Производство</h1>
        {loading && <span className="badge">Загрузка…</span>}
      </div>
      {err && <div className="error">{err}</div>}

      <div className="tableWrap">
        <table className="table fancy">
          <thead>
            <tr>
              <th style={{ width: "26%" }}>Клиент</th>
              <th style={{ width: "16%" }}>Статус</th>
              <th style={{ width: "14%" }}>Формулы</th>
              <th style={{ width: "20%" }}>Действие</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const row = openRows[u.id];
              const isOpen = row?.open;
              return (
                <>
                  <tr key={u.id} className={isOpen ? "tr--open" : ""}>
                    <td>
                      <div className="cellTitle">{u.name || "—"}</div>
                      <div className="cellSub">{u.email}</div>
                    </td>
                    <td>
                      <StatusBadge status={u.status} />
                    </td>

                    <td>
                      {u.status !== "draft" ? (
                        <button
                          className="btn btn--ghost"
                          onClick={() => toggleOpen(u)}
                          aria-expanded={isOpen}
                        >
                          {isOpen ? "Свернуть" : "Открыть"}
                          <span
                            className={`chev ${isOpen ? "chev--up" : ""}`}
                          />
                        </button>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>

                    <td>
                      <button
                        className="btn btn--primary"
                        onClick={() => setConfirmUser(u)}
                        disabled={
                          u.status !== "approved" &&
                          u.status !== "in_production"
                        }
                      >
                        {NextActionLabel(u.status)}
                      </button>
                    </td>
                  </tr>

                  {isOpen && (
                    <tr className="trDetails">
                      <td colSpan={5}>
                        {row?.loading && (
                          <div className="muted">Загрузка парфюмов…</div>
                        )}
                        {row?.error && <div className="error">{row.error}</div>}
                        {!row?.loading && !row?.error && (
                          <AdminParfumesTable
                            parfumes={row.parfumes}
                            onSave={async (parfumeId, patch) => {
                              // дерни свой API:
                              await api(
                                `/admin/user/${u.id}/parfume/${parfumeId}`,
                                {
                                  method: "PUT",
                                  body: JSON.stringify(patch),
                                }
                              );
                              // локально обнови состояние строки (как уже делаешь в saveParfume)
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
            {users.length === 0 && (
              <tr>
                <td colSpan={5} className="muted">
                  Нет клиентов в этом разделе
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* confirm modal */}
      {confirmUser && (
        <div className="modal">
          <div className="modal__box">
            <div className="modal__title">Подтверждение действия</div>
            <div className="modal__text">
              Перевести <b>{confirmUser.name}</b> ({confirmUser.email || "—"})
              на следующий шаг?
            </div>
            <div className="modal__actions">
              <button
                className="btn btn--danger"
                onClick={() => setConfirmUser(null)}
              >
                Отмена
              </button>
              <button
                className="btn btn--primary"
                onClick={() => advance(confirmUser)}
              >
                Да, продолжить
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
